import React from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Checkbox,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import type { DashboardFilterOptions, DashboardFilters as DashboardFilterState } from '../../hooks/useDashboardData';

interface DashboardFiltersProps {
  filters: DashboardFilterState;
  options: DashboardFilterOptions;
  onFiltersChange: (updated: Partial<DashboardFilterState>) => void;
  onReset: () => void;
}

type MultiSelectKey = keyof Pick<DashboardFilterState, 'departments' | 'occupations' | 'timings' | 'reasons'>;

const DashboardFiltersPanel: React.FC<DashboardFiltersProps> = ({
  filters,
  options,
  onFiltersChange,
  onReset,
}) => {
  const handleMultiSelectChange = (key: MultiSelectKey) => (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    onFiltersChange({ [key]: value } as Partial<DashboardFilterState>);
  };

  const renderMultiSelect = (label: string, key: MultiSelectKey, items: string[]) => (
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        label={label}
        value={filters[key]}
        onChange={handleMultiSelectChange(key)}
        renderValue={selected => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {(selected as string[]).map(value => (
              <Chip key={value} label={value} size="small" />
            ))}
          </Box>
        )}
      >
        {items.map(item => (
          <MenuItem key={item} value={item}>
            <Checkbox checked={(filters[key] as string[]).indexOf(item) > -1} />
            <ListItemText primary={item} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle1" sx={{ minWidth: 120 }}>フィルタ</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField
            label="開始日"
            type="date"
            size="small"
            value={filters.dateRange.start || ''}
            onChange={event => onFiltersChange({
              dateRange: { ...filters.dateRange, start: event.target.value || undefined },
            })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="終了日"
            type="date"
            size="small"
            value={filters.dateRange.end || ''}
            onChange={event => onFiltersChange({
              dateRange: { ...filters.dateRange, end: event.target.value || undefined },
            })}
            InputLabelProps={{ shrink: true }}
          />
          {renderMultiSelect('病棟/部署', 'departments', options.departments)}
          {renderMultiSelect('職種', 'occupations', options.occupations)}
          {renderMultiSelect('場面', 'timings', options.timings)}
          {renderMultiSelect('理由', 'reasons', options.reasons)}
          <Button
            variant="outlined"
            color="secondary"
            onClick={onReset}
            sx={{ whiteSpace: 'nowrap' }}
          >
            リセット
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default DashboardFiltersPanel;
